import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { costoDeUnidadVenta, diasEntre, formatBp, formatMoney, newId, parseMoney, sumarDias, ZERO_COST, type Centavos, type UnitCost } from '@abastece/shared';
import { DIAS_MAXIMOS_OFERTA, type MotivoOferta, type Producto } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { precioDe, unidadesDe } from '../lib/catalogo.js';
import { costoConocido } from '../lib/costos.js';
import { emitirYVer } from '../lib/emitir.js';
import { botonesDeDescuento, comparacionDeOferta, llevaParecido, menosDe, nombreDeLleva, precioConDescuento, type Lleva, type SugerenciaOferta } from '../lib/ofertas.js';
import { usePuede } from '../lib/sesion.js';
import { useT, type Clave } from '../i18n/index.js';
import { etiquetaPresentacion } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';
import { Campo } from './Campo.js';
import { fechaCorta } from './CuentasDeCaja.js';
import { Ico } from './Iconos.js';
import { MargenDe } from './Margen.js';
import { Modal } from './Modal.js';
import { Destacado, FilaAntesDespues, GrupoAntesDespues, Insignia, Motivo, PrecioAntes, Sugerido, TablaAntesDespues, Tachado } from './PreciosDeOferta.js';

/** The shares off a tap sets every price to. */
const DESCUENTOS = [10, 15, 20, 25, 30, 40, 50] as const;

/** So many for fewer, a tap each. */
const LLEVAS: readonly Lleva[] = [
  { cantidad: 2, paga: 1 },
  { cantidad: 3, paga: 2 },
  { cantidad: 4, paga: 3 },
  { cantidad: 5, paga: 4 },
];

/** The two kinds of offer: a lower price, or so many for fewer. */
type Tipo = 'precio' | 'lleva';
const TIPOS: readonly { tipo: Tipo; etiqueta: Clave }[] = [
  { tipo: 'precio', etiqueta: 'ofertas.hoja.tipoPrecio' },
  { tipo: 'lleva', etiqueta: 'ofertas.hoja.tipoLleva' },
];

/** A price as a box shows it: «21», «21.50». */
const comoTexto = (c: number) => (c % 100 === 0 ? String(c / 100) : (c / 100).toFixed(2));

/** A whole number typed in a box, or null. */
const entero = (texto: string) => (/^\s*\d+\s*$/.test(texto) ? Number(texto) : null);

/** «SUGERIDO» inside a button: white on the pressed one, teal on the rest. */
function MarcaSugerido() {
  const t = useT();
  return (
    <span className="rounded bg-teal-400/15 px-1 py-px text-[10px] leading-tight font-bold tracking-wide text-teal-200 uppercase group-aria-pressed:bg-white/20 group-aria-pressed:text-white">
      {t('ofertas.sugerido')}
    </span>
  );
}

/** What one sold leaves, in a cell of the table: under nothing a loss, in red; with nothing to work it out from, «—». */
function Ganancia({ monto }: { monto: Centavos | null }) {
  if (monto === null) return <span className="text-xs text-slate-600">—</span>;
  return <span className={`text-sm ${monto < 0 ? 'text-rose-300' : 'text-slate-300'}`}>{formatMoney(monto)}</span>;
}

/**
 * «Poner en oferta»: a product's prices lower on the shop's list for some days (`oferta.creada`),
 * from a suggestion of «Ofertas», filled in with it, or by hand.
 *
 * Wider than other sheets on a laptop (the owner, 2026-11-03: «can we widen the form or something so
 * that it clearly shows a before and after column, also align everything more nicely»). From a
 * suggestion, at its top, «Sugerido» with how much it takes off, for how many days, and why
 * (`Sugerido`); the sheet opens at it (the owner: «it's not obvious to me what the application is
 * suggesting … i need to see original price -> suggested price»).
 *
 * One table for every presentation with a price on the list (`TablaAntesDespues`): its columns
 * «Antes» and «En oferta», each presentation's rows under its name, what one costs and how much the
 * offer takes off, «−30%»: «Precio», the list's struck once its box holds an offer, and the box; and
 * for whoever sees costs «Margen» and «Ganancia» before and on offer (the owner: «you gotta show the
 * margin we're at when adding the promotional price or at least label it obviously»). Under them,
 * what the customer saves, the suggested price when the box says another, «Bajo el mínimo de 10%»
 * under the shop's floor. A row of shares off sets every box at once, down to the fifty centavos,
 * and shows pressed the one every box is at; the suggestion's says «Sugerido», and the sheet opens
 * with it pressed. A box left empty, «Sin oferta», leaves that presentation at its price. Under what
 * it costs is said, and what is lost on each one sold; it is allowed, as the owner's decision: an
 * expiring batch brings nothing once its date has gone, and the sheet says what that would be.
 *
 * Or «Lleva y paga» (2026-11-03, the plan's third part: «2×1», «lleva 3, paga 2»): so many for the
 * price of fewer, at the list's price, «2×1», «3×2», «4×3», «5×4» a tap each or typed, on the
 * presentations ticked in the same table, the smallest to begin with: each ticked says «Precio de
 * 3», what so many come to at the list's price struck and at the offer, how much off that is, and for
 * whoever sees costs «Margen» and «Ganancia» of so many before and on offer; under it, said as with a
 * price. From a suggestion, the one nearest its share off that does not go under the cost, pressed
 * and «Sugerido» (a button of its own when it is none of the four).
 *
 * From when to when, today at the earliest and three months at the most; and a note, for whoever
 * reads «Bitácora». The books refuse one that overlaps another of the product's on the list, saying
 * which.
 */
export function HojaOferta({
  producto,
  listaId,
  sugerencia,
  porque,
  hoy,
  onPuesta,
  onCerrar,
}: {
  producto: Producto;
  /** The shop's own list: the one offers go on. */
  listaId: string;
  sugerencia?: SugerenciaOferta | undefined;
  /** Why it is suggested, as its row says it. */
  porque?: string | undefined;
  hoy: string;
  onPuesta: () => void;
  onCerrar: () => void;
}) {
  const t = useT();
  const nombre = useNombreProducto();
  const verCostos = usePuede('ver_costos');
  const unidades = useLiveQuery(() => db.unidades.where('productoId').equals(producto.id).toArray(), [producto.id]);
  const precios = useLiveQuery(() => db.precios.toArray(), [], []);
  const config = useLiveQuery(() => readSettings(), [], null);
  const existencias = useLiveQuery(
    () => (verCostos ? db.existencias.where('productoId').equals(producto.id).toArray() : []),
    [producto.id, verCostos],
    [],
  );
  const compras = useLiveQuery(() => (verCostos ? db.compras.toArray() : []), [verCostos], []);

  /**
   * From a suggestion, the so many for fewer nearest its share off (`llevaParecido`): «Lleva y paga»
   * starts at it, and its button says «Sugerido». Null by hand, or with none that covers the cost.
   */
  const primeraSugerida = sugerencia?.precios[0];
  const llevaSugerido =
    primeraSugerida && sugerencia ? llevaParecido(sugerencia.descuentoBp, primeraSugerida.regular, primeraSugerida.costo, sugerencia.motivo === 'vence') : null;

  const [tipo, setTipo] = useState<Tipo>('precio');
  const [escritos, setEscritos] = useState<Record<string, string>>(() =>
    Object.fromEntries((sugerencia?.precios ?? []).map((p) => [p.unidadVentaId, comoTexto(p.oferta)])),
  );
  /** So many for fewer as typed: from a suggestion, the nearest to its share off; otherwise 2×1. */
  const [llevaTexto, setLlevaTexto] = useState(() => ({ cantidad: String(llevaSugerido?.cantidad ?? 2), paga: String(llevaSugerido?.paga ?? 1) }));
  /** The presentations ticked for it; null until one is, the smallest. */
  const [elegidas, setElegidas] = useState<ReadonlySet<string> | null>(null);
  const [desde, setDesde] = useState(sugerencia?.desde ?? hoy);
  const [hasta, setHasta] = useState(sugerencia?.hasta ?? sumarDias(hoy, 13));
  const [nota, setNota] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const motivo: MotivoOferta = sugerencia?.motivo ?? 'otro';
  const costoBase: UnitCost = verCostos ? costoConocido(producto.id, 'tienda', existencias, compras) : ZERO_COST;
  const minimoBp = config?.precioMargenMinimoBp ?? 1_000;

  /** Each presentation with a price on the list: its box, and what it says. */
  const cajas = unidadesDe(unidades ?? [], producto.id).flatMap((u) => {
    const regular = precioDe(precios, u.id, listaId);
    if (regular === null || regular <= 0) return [];
    const texto = escritos[u.id] ?? '';
    const leido = texto.trim() === '' ? null : parseMoney(texto);
    const oferta = leido !== null && leido > 0 ? (leido as Centavos) : null;
    const costo = costoBase > 0 ? costoDeUnidadVenta(costoBase, u.factor) : null;
    return [
      {
        u,
        regular,
        texto,
        oferta,
        costo,
        mal: texto.trim() !== '' && oferta === null,
        noBaja: oferta !== null && oferta >= regular,
        bajoCosto: oferta !== null && costo !== null && oferta < costo,
      },
    ];
  });
  const enOferta = cajas.filter((c) => c.oferta !== null && !c.noBaja);

  // So many for fewer: as typed, whole, two to fifty, at least one fewer paid.
  const cantidad = entero(llevaTexto.cantidad);
  const paga = entero(llevaTexto.paga);
  const lleva: Lleva | null = cantidad !== null && paga !== null && cantidad >= 2 && cantidad <= 50 && paga >= 1 && paga < cantidad ? { cantidad, paga } : null;
  const marcadas = elegidas ?? new Set(cajas.slice(0, 1).map((c) => c.u.id));
  const conLleva = cajas
    .filter((c) => marcadas.has(c.u.id))
    .map((c) => {
      const porTodas = lleva ? ((c.regular * lleva.paga) as Centavos) : null;
      const cuestan = lleva && c.costo !== null ? ((c.costo * lleva.cantidad) as Centavos) : null;
      return { ...c, porTodas, cuestan, pierde: porTodas !== null && cuestan !== null && cuestan > porTodas ? ((cuestan - porTodas) as Centavos) : null };
    });

  const dias = desde && hasta ? diasEntre(desde, hasta) + 1 : 0;
  const malasFechas = !desde || !hasta || desde < hoy ? t('ofertas.hoja.desdeHoy') : hasta < desde ? t('ofertas.hoja.alReves') : dias > DIAS_MAXIMOS_OFERTA ? t('ofertas.hoja.muyLarga', { n: DIAS_MAXIMOS_OFERTA }) : null;
  const listo =
    unidades !== undefined &&
    malasFechas === null &&
    !ocupado &&
    (tipo === 'precio' ? enOferta.length > 0 && !cajas.some((c) => c.mal || c.noBaja) : lleva !== null && conLleva.length > 0);
  const bajoCosto = cajas.filter((c) => c.bajoCosto);
  const pierdenLleva = conLleva.filter((c) => c.pierde !== null);

  /** «−20%»: every box at that much off its price, down to the fifty centavos. */
  function aTodas(pct: number) {
    setEscritos(Object.fromEntries(cajas.map((c) => [c.u.id, comoTexto(precioConDescuento(c.regular, pct))])));
  }
  /** The share off every box is at, so its button shows pressed. */
  const enTodas = (pct: number) => cajas.length > 0 && cajas.every((c) => c.oferta !== null && c.oferta === precioConDescuento(c.regular, pct));

  /**
   * The suggestion among the shares off (the owner, 2026-11-03: «it should clearly show suggested
   * discount as the default highlighted option»): the sheet opens at it, so its button opens pressed,
   * with «Sugerido» on it; which button that is, `botonesDeDescuento` says. Pressed, it writes the
   * suggestion's prices as they are, and leaves empty a presentation it left out.
   */
  const sugeridos = new Map((sugerencia?.precios ?? []).map((p) => [p.unidadVentaId, p.oferta]));
  const { botones } = botonesDeDescuento(
    DESCUENTOS,
    cajas.map((c) => ({ unidadVentaId: c.u.id, regular: c.regular })),
    sugerencia,
  );
  /** How much the suggestion takes off, as its button says it, for «Sugerido» at the top. */
  const botonSugerido = botones.find((b) => b.sugerido);
  const quitaSugerida = botonSugerido?.pct != null ? `−${botonSugerido.pct}%` : null;
  const enLoSugerido = sugeridos.size > 0 && cajas.length > 0 && cajas.every((c) => (sugeridos.has(c.u.id) ? c.oferta === sugeridos.get(c.u.id) : c.texto.trim() === ''));
  function aLoSugerido() {
    setEscritos(Object.fromEntries(cajas.map((c) => [c.u.id, sugeridos.has(c.u.id) ? comoTexto(sugeridos.get(c.u.id)!) : ''])));
  }
  /** So many for fewer a tap each, with the suggestion's among them when it is none of them. */
  const llevas =
    llevaSugerido && !LLEVAS.some((x) => x.cantidad === llevaSugerido.cantidad && x.paga === llevaSugerido.paga)
      ? [...LLEVAS, llevaSugerido].sort((a, b) => a.cantidad - b.cantidad)
      : LLEVAS;

  function marcar(unidadVentaId: string, si: boolean) {
    const nuevas = new Set(marcadas);
    if (si) nuevas.add(unidadVentaId);
    else nuevas.delete(unidadVentaId);
    setElegidas(nuevas);
  }

  async function poner() {
    if (!listo) return;
    setOcupado(true);
    setError(null);
    try {
      const { rechazo } = await emitirYVer('oferta.creada', {
        ofertaId: newId(),
        productoId: producto.id,
        listaId,
        precios:
          tipo === 'lleva' && lleva
            ? conLleva.map((c) => ({ unidadVentaId: c.u.id, lleva: lleva.cantidad, paga: lleva.paga }))
            : enOferta.map((c) => ({ unidadVentaId: c.u.id, precio: c.oferta! })),
        desde,
        hasta,
        motivo,
        ...(nota.trim() ? { nota: nota.trim() } : {}),
      });
      if (rechazo) {
        setOcupado(false);
        setError(rechazo);
        return;
      }
    } catch (e) {
      setOcupado(false);
      setError(e instanceof Error ? e.message : t('ofertas.hoja.noSePudo'));
      return;
    }
    onPuesta();
    onCerrar();
  }

  const sinPrecio = unidades !== undefined && cajas.length === 0;

  return (
    <Modal
      titulo={t('ofertas.hoja.titulo', { producto: nombre(producto) })}
      onCerrar={onCerrar}
      ancho="amplio"
      pie={
        <button type="button" className="btn-primario w-full" disabled={!listo} onClick={() => void poner()} data-oferta-poner>
          <Ico icono="precios" />
          {t('ofertas.poner')}
        </button>
      }
    >
      <div className="flex flex-col gap-5" data-oferta-hoja={producto.id}>
        {/* What is suggested: how much off, for how long, and why. Its prices are the table's as the sheet opens. */}
        {sugerencia && (
          <Sugerido sugerencia={sugerencia} unidadBase={producto.unidadBase} verCostos={verCostos} minimoBp={minimoBp} conTabla={false} quita={quitaSugerida}>
            {porque && (
              <p className="mt-2 border-t border-white/5 pt-2 text-xs text-slate-400">
                <Motivo motivo={sugerencia.motivo} />
                <span data-oferta-porque>{porque}</span>
              </p>
            )}
          </Sugerido>
        )}

        {/* A lower price, or so many for fewer. */}
        <div className="flex gap-1 rounded-xl bg-slate-800 p-1" role="group" aria-label={t('ofertas.hoja.tipo')}>
          {TIPOS.map((x) => (
            <button
              key={x.tipo}
              type="button"
              aria-pressed={tipo === x.tipo}
              onClick={() => setTipo(x.tipo)}
              data-sin-icono="pestana"
              data-oferta-tipo={x.tipo}
              className={`min-h-10 flex-1 rounded-lg px-2.5 py-1 text-sm leading-tight transition ${
                tipo === x.tipo ? 'bg-marca-600 font-medium text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
              }`}
            >
              {t(x.etiqueta)}
            </button>
          ))}
        </div>

        {sinPrecio ? (
          <p className="text-sm text-amber-300" data-oferta-sin-precio>
            {t('ofertas.hoja.sinPrecio')}
          </p>
        ) : tipo === 'precio' ? (
          <>
            {/* A tap sets them all; each box can then be changed by itself. */}
            <div>
              <p className="etiqueta">{t('ofertas.hoja.descuento')}</p>
              <div className="flex flex-wrap gap-1.5">
                {botones.map((b) => (
                  <button
                    key={b.clave}
                    type="button"
                    aria-pressed={b.sugerido ? enLoSugerido : enTodas(b.pct!)}
                    className={`group tabular inline-flex h-10 min-w-14 items-center justify-center gap-1.5 rounded-lg bg-slate-800 px-3 text-sm text-slate-200 ring-1 transition ring-inset hover:bg-slate-700 aria-pressed:bg-marca-600 aria-pressed:font-semibold aria-pressed:text-white ${
                      b.sugerido ? 'ring-teal-400/50' : 'ring-white/5'
                    }`}
                    onClick={() => (b.sugerido ? aLoSugerido() : aTodas(b.pct!))}
                    data-sin-icono="tecla"
                    data-oferta-descuento={b.clave}
                    data-oferta-descuento-sugerido={b.sugerido ? '' : undefined}
                  >
                    {b.pct !== null && `−${b.pct}%`}
                    {b.sugerido && <MarcaSugerido />}
                  </button>
                ))}
              </div>
            </div>

            {/* Every presentation in one table: before, and on offer, the box in its column. */}
            <div className="rounded-xl bg-slate-950/40 p-3 ring-1 ring-white/5 ring-inset" data-oferta-precios-hoja>
              <TablaAntesDespues>
                {cajas.map((c, i) => {
                  const presentacion = etiquetaPresentacion(c.u, producto.unidadBase);
                  const oferta = c.oferta !== null && !c.noBaja ? c.oferta : null;
                  const antes = comparacionDeOferta(c.regular, { precio: c.regular }, c.costo);
                  const ahora = oferta !== null ? comparacionDeOferta(c.regular, { precio: oferta }, c.costo) : null;
                  const sugerido = sugeridos.get(c.u.id);
                  const otroQueElSugerido = sugerido !== undefined && sugerido !== c.oferta;
                  // Over nothing and under the shop's floor; at nothing or under it, the badge's red says it.
                  const bajoMinimo = ahora?.margenAhora != null && 0 < ahora.margenAhora && ahora.margenAhora < minimoBp;
                  return (
                    <GrupoAntesDespues
                      key={c.u.id}
                      id={c.u.id}
                      primero={i === 0}
                      presentacion={presentacion}
                      detalle={c.costo !== null && <span data-oferta-costo>{t('ofertas.hoja.cuesta', { monto: formatMoney(c.costo) })}</span>}
                      insignia={oferta !== null && <Insignia>{menosDe(oferta, c.regular)}</Insignia>}
                    >
                      <FilaAntesDespues
                        etiqueta={t('ofertas.tabla.precio')}
                        dato="precio"
                        antes={<PrecioAntes monto={c.regular} tachado={oferta !== null} />}
                        ahora={
                          <input
                            className={`campo tabular h-11 min-h-0 w-full max-w-40 min-w-0 px-3 text-right text-lg placeholder:text-sm max-[359px]:px-2 ${c.mal || c.noBaja ? 'border-rose-500' : ''}`}
                            inputMode="decimal"
                            placeholder={t('ofertas.hoja.sinOferta')}
                            aria-label={t('ofertas.hoja.precioDe', { presentacion })}
                            aria-invalid={c.mal || c.noBaja}
                            value={c.texto}
                            onChange={(e) => setEscritos((x) => ({ ...x, [c.u.id]: e.target.value }))}
                            data-oferta-precio={c.u.id}
                          />
                        }
                      />
                      {/* For whoever sees costs; the figures on offer end where the box's do. */}
                      {c.costo !== null && (
                        <>
                          <FilaAntesDespues
                            etiqueta={t('ofertas.tabla.margen')}
                            dato="margen"
                            antes={<MargenDe precio={c.regular} costo={c.costo} minimoBp={minimoBp} />}
                            ahora={
                              <span className="pr-3">
                                <MargenDe precio={oferta} costo={c.costo} minimoBp={minimoBp} />
                              </span>
                            }
                          />
                          <FilaAntesDespues
                            etiqueta={t('ofertas.tabla.ganancia')}
                            dato="ganancia"
                            antes={<Ganancia monto={antes.gananciaAntes} />}
                            ahora={
                              <span className="pr-3">
                                <Ganancia monto={ahora?.gananciaAhora ?? null} />
                              </span>
                            }
                          />
                        </>
                      )}
                      {(c.noBaja || oferta !== null || otroQueElSugerido || bajoMinimo || (verCostos && c.costo === null)) && (
                        <div className="col-span-full flex flex-wrap gap-x-3 gap-y-1 text-xs">
                          {c.noBaja && <p className="basis-full text-sm text-rose-400">{t('ofertas.hoja.noBaja')}</p>}
                          {oferta !== null && (
                            <p className="tabular text-teal-300" data-oferta-ahorra>
                              {t('ofertas.hoja.ahorra', { monto: formatMoney((c.regular - oferta) as Centavos) })}
                            </p>
                          )}
                          {otroQueElSugerido && (
                            <p className="tabular text-slate-400" data-oferta-sugerido-era>
                              {t('ofertas.hoja.sugeridoEra', { monto: formatMoney(sugerido) })}
                            </p>
                          )}
                          {bajoMinimo && (
                            <p className="text-amber-300" data-oferta-bajo-minimo>
                              {t('ofertas.hoja.bajoMinimo', { minimo: formatBp(minimoBp) })}
                            </p>
                          )}
                          {verCostos && c.costo === null && (
                            <p className="text-slate-500" data-oferta-sin-costo>
                              {t('ofertas.hoja.sinCosto')}
                            </p>
                          )}
                        </div>
                      )}
                    </GrupoAntesDespues>
                  );
                })}
              </TablaAntesDespues>
            </div>
          </>
        ) : (
          <>
            {/* So many for fewer: a tap each, or typed. */}
            <div>
              <p className="etiqueta">{t('ofertas.hoja.llevaCual')}</p>
              <div className="flex flex-wrap gap-1.5">
                {llevas.map((x) => {
                  const esSugerido = llevaSugerido?.cantidad === x.cantidad && llevaSugerido.paga === x.paga;
                  return (
                    <button
                      key={nombreDeLleva(x)}
                      type="button"
                      aria-pressed={lleva?.cantidad === x.cantidad && lleva.paga === x.paga}
                      className={`group tabular inline-flex h-10 min-w-14 items-center justify-center gap-1.5 rounded-lg bg-slate-800 px-3 text-sm text-slate-200 ring-1 transition ring-inset hover:bg-slate-700 aria-pressed:bg-marca-600 aria-pressed:text-white ${
                        esSugerido ? 'ring-teal-400/50' : 'ring-white/5'
                      }`}
                      onClick={() => setLlevaTexto({ cantidad: String(x.cantidad), paga: String(x.paga) })}
                      data-sin-icono="tecla"
                      data-oferta-lleva-de={nombreDeLleva(x)}
                      data-oferta-lleva-sugerido={esSugerido ? '' : undefined}
                    >
                      {nombreDeLleva(x)}
                      {esSugerido && <MarcaSugerido />}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Campo
                etiqueta={t('ofertas.hoja.lleva')}
                inputMode="numeric"
                className="tabular"
                value={llevaTexto.cantidad}
                onChange={(e) => setLlevaTexto((x) => ({ ...x, cantidad: e.target.value }))}
                autoComplete="off"
                data-oferta-lleva
              />
              <Campo
                etiqueta={t('ofertas.hoja.paga')}
                inputMode="numeric"
                className="tabular"
                value={llevaTexto.paga}
                onChange={(e) => setLlevaTexto((x) => ({ ...x, paga: e.target.value }))}
                autoComplete="off"
                data-oferta-paga
              />
            </div>
            {lleva === null && (
              <p className="-mt-3 text-sm text-rose-400" data-oferta-lleva-mal>
                {t('ofertas.hoja.llevaMal')}
              </p>
            )}
            {/* Which presentations, ticked in the same table: each ticked with what so many come to, before and with the deal. */}
            <div className="rounded-xl bg-slate-950/40 p-3 ring-1 ring-white/5 ring-inset" data-oferta-lleva-presentaciones>
              <TablaAntesDespues>
                {cajas.map((c, i) => {
                  const marcada = marcadas.has(c.u.id);
                  const v = marcada && lleva ? comparacionDeOferta(c.regular, { lleva }, c.costo) : null;
                  const presentacion = etiquetaPresentacion(c.u, producto.unidadBase);
                  return (
                    <GrupoAntesDespues
                      key={c.u.id}
                      id={c.u.id}
                      primero={i === 0}
                      presentacion={
                        <label className="flex cursor-pointer items-center gap-2.5" data-oferta-lleva-presentacion={c.u.id}>
                          <input type="checkbox" checked={marcada} onChange={(e) => marcar(c.u.id, e.target.checked)} className="size-5 shrink-0 accent-teal-500" />
                          {presentacion}
                        </label>
                      }
                      detalle={
                        <>
                          {formatMoney(c.regular)}
                          {c.costo !== null && <span data-oferta-costo> · {t('ofertas.hoja.cuesta', { monto: formatMoney(c.costo) })}</span>}
                        </>
                      }
                      insignia={v?.baja && lleva && <Insignia>−{Math.round(((lleva.cantidad - lleva.paga) * 100) / lleva.cantidad)}%</Insignia>}
                    >
                      {v && v.antes !== null && v.ahora !== null && (
                        <>
                          <FilaAntesDespues
                            etiqueta={t('ofertas.tabla.precioDe', { n: v.cuantas })}
                            dato="precio"
                            antes={<Tachado monto={v.antes} />}
                            ahora={<Destacado monto={v.ahora} />}
                          />
                          {v.costo !== null && (
                            <>
                              <FilaAntesDespues
                                etiqueta={t('ofertas.tabla.margen')}
                                dato="margen"
                                antes={<MargenDe precio={v.antes} costo={v.costo} minimoBp={minimoBp} />}
                                ahora={<MargenDe precio={v.ahora} costo={v.costo} minimoBp={minimoBp} />}
                              />
                              <FilaAntesDespues
                                etiqueta={t('ofertas.tabla.ganancia')}
                                dato="ganancia"
                                antes={<Ganancia monto={v.gananciaAntes} />}
                                ahora={<Ganancia monto={v.gananciaAhora} />}
                              />
                            </>
                          )}
                        </>
                      )}
                    </GrupoAntesDespues>
                  );
                })}
              </TablaAntesDespues>
            </div>
          </>
        )}

        {/* Under the cost: what each one sold loses, or so many; for an expiring batch, what is lost without it. */}
        {(tipo === 'precio' ? bajoCosto.length > 0 : pierdenLleva.length > 0) && (
          <div className="rounded-xl bg-amber-500/10 px-4 py-3 text-sm text-amber-200 ring-1 ring-amber-500/20 ring-inset" data-oferta-bajo-costo>
            <p>
              {tipo === 'precio'
                ? bajoCosto
                    .map((c) =>
                      t('ofertas.hoja.bajoCosto', {
                        presentacion: etiquetaPresentacion(c.u, producto.unidadBase),
                        pierde: formatMoney((c.costo! - c.oferta!) as Centavos),
                      }),
                    )
                    .join(' ')
                : pierdenLleva
                    .map((c) =>
                      t('ofertas.hoja.llevaPierde', {
                        presentacion: etiquetaPresentacion(c.u, producto.unidadBase),
                        n: lleva!.cantidad,
                        pierde: formatMoney(c.pierde!),
                      }),
                    )
                    .join(' ')}
            </p>
            {sugerencia?.vence && (
              <p className="mt-1 text-amber-200/80">{t('ofertas.hoja.mejorQueBotar', { monto: formatMoney(sugerencia.enJuego), fecha: fechaCorta(sugerencia.vence.fecha) })}</p>
            )}
          </div>
        )}

        {/* Side by side where a date has room whole; one over the other on a narrow phone, the year cut otherwise. */}
        <div className="grid gap-3 min-[400px]:grid-cols-2">
          <Campo etiqueta={t('ofertas.hoja.desde')} type="date" min={hoy} value={desde} onChange={(e) => setDesde(e.target.value)} data-oferta-desde />
          <Campo etiqueta={t('ofertas.hoja.hasta')} type="date" min={desde || hoy} value={hasta} onChange={(e) => setHasta(e.target.value)} data-oferta-hasta />
        </div>
        <p className={`-mt-3 text-sm ${malasFechas ? 'text-rose-400' : 'text-slate-500'}`} data-oferta-dias>
          {malasFechas ?? (dias === 1 ? t('ofertas.hoja.unDia') : t('ofertas.hoja.dias', { n: dias }))}
        </p>

        <Campo etiqueta={t('ofertas.hoja.nota')} maxLength={300} value={nota} onChange={(e) => setNota(e.target.value)} autoComplete="off" data-oferta-nota />

        {error && (
          <p className="text-sm text-rose-400" data-oferta-error>
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
